// src/components/dashboards/common/DashboardBackButton.tsx
// -----------------------------------------------------------------------------
// DashboardBackButton — single back-arrow control shared by:
//   - NL query (header bar when an answer is being shown)
//   - V2 patient creation (top-left, dismisses the form)
//
// Joan, 2026-05-27: the previous NL back arrow used a quiet navy-tinted glass
// that disappeared into the page. This version is a primary-gradient pill
// (white icon, primary.main → primary.dark gradient, lift on hover) so the
// affordance is unambiguous and theme-aligned.
// -----------------------------------------------------------------------------

import { Button, IconButton, Tooltip, alpha, useTheme } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import React from "react";

import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../../theme/palette";
import { glassControl } from "../../../theme/glassTokens";

export interface DashboardBackButtonProps {
  onClick: () => void;
  /** Optional override for the tooltip (defaults to t("action.back", "Back")). */
  tooltip?: string;
  /** Disable when an outgoing async action is in flight. */
  disabled?: boolean;
  /**
   * Render as the row's PRIMARY action rather than a quiet arrow.
   *
   * Used where Back is the only thing to do with the row: inside patient
   * creation and inside an NL answer, "Add patient" is hidden (you are already
   * there), so Back inherits the CTA's slot, geometry and colour. The quiet
   * navy circle was invisible on the dark ground anyway — primary.main is
   * deepNavy, the same colour it sat on.
   */
  prominent?: boolean;
}

const DashboardBackButton: React.FC<DashboardBackButtonProps> = ({
  onClick,
  tooltip,
  disabled,
  prominent,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const title = tooltip || t("action.back", "Back");

  if (prominent) {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        startIcon={<ArrowBackIcon />}
        sx={{
          flexShrink: 0,
          height: glassControl.height,
          minHeight: glassControl.height,
          px: 2.5,
          borderRadius: glassControl.radius,
          gap: 1,
          textTransform: "none",
          fontWeight: 700,
          fontSize: "0.9rem",
          whiteSpace: "nowrap",
          // Deliberately the same recipe as the Add-patient CTA, including
          // deepNavy ink: white on #FF8C00 is 2.59:1.
          background: `linear-gradient(180deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
          color: athenaColors.deepNavy,
          boxShadow: `0 4px 16px ${alpha(athenaColors.orange, 0.3)}`,
          transition: "all .2s ease",
          "&:hover": {
            background: `linear-gradient(180deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 16px ${alpha(athenaColors.orange, 0.42)}`,
          },
          "&:active": { transform: "translateY(0)" },
          "&.Mui-disabled": {
            background: alpha(theme.palette.common.white, 0.08),
            color: alpha(theme.palette.common.white, 0.35),
          },
        }}
      >
        {title}
      </Button>
    );
  }

  return (
    <Tooltip title={title} arrow placement="right">
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          aria-label={title}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "50%",
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.98,
            )} 0%, ${alpha(theme.palette.primary.main, 0.86)} 100%)`,
            color: theme.palette.common.white,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            boxShadow: theme.custom?.glassShadow,
            backdropFilter: "blur(6px)",
            transition: "all .25s ease",
            "&:hover": {
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.primary.dark,
                1,
              )} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
              transform: "translateY(-1px)",
              boxShadow: `${theme.custom?.glassShadow}, 0 4px 12px ${alpha(
                theme.palette.primary.dark,
                0.3,
              )}`,
            },
            "&.Mui-disabled": {
              background: alpha(theme.palette.common.white, 0.08),
              color: alpha(theme.palette.common.white, 0.35),
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default DashboardBackButton;
